/**
 * ShipmentsTable.js
 *
 * Full-featured shipments table.
 * Unique responsibilities vs PartialShipmentsTab:
 *  - Bulk invoice creation & miles refresh
 *  - Portal-rendered action dropdown (avoids stacking-context clipping)
 *  - Auto-grow / resizable columns + expanded-cell-on-hover
 *  - Advanced filters persisted in Redux (survives tab switches)
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { MoreHorizontal } from "lucide-react";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import Cookies from "js-cookie";
import { useSelector, useDispatch } from "react-redux";
import { addTab, setListViewState, setStatusFilter } from "../../../../redux/slices/shipmentSlice";
import { usePrivilege } from "../../../../guard/usePrivilege";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { addTab as addInvoiceTab, setActiveTab as setActiveInvoiceTab } from "../../../../redux/slices/invoiceSlice";
import { useNavigate } from "react-router-dom";
import { fallbackName } from "../../Invoice/Utils/util";
import { SORTABLE_COLUMNS, FILTER_TABS } from "./Utils/sortableColumns";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";

import {
  TABLE_HEADERS,
  DISABLED_STATUSES,
  makeKey,
  buildShipmentSearchPayload,
  formatShipmentRow,
  useShipmentFilters,
  AdvancedFilterToolbar,
  AdvancedFilterModal,
  DEFAULT_ADVANCED_FILTERS,
  DEFAULT_QUICK_FILTER,
} from "./shipmentShared";
import {
  useTableSavedViews,
  TableSavedViewsControl,
} from "../../../TableSavedViews";
import { setAdvancedFilters } from "../../../../redux/slices/shipmentSlice";

// ─── Layout constants (unique to this view's resizable/expandable cell setup) ──

const DATA_COLUMN_MIN_WIDTH    = 200;
const DATA_COLUMN_MAX_WIDTH    = 420;
const SELECTION_COLUMN_WIDTH   = 80;
const ACTION_COLUMN_WIDTH      = 50;
const SHIPMENT_NUMBER_HEADER   = "Shipment Number";

// ─── Main Component ───────────────────────────────────────────────────────────

const ShipmentsTable = ({
  searchQuery,
  filter,
  searchTrigger,
  setTotalShipments,
  stateKey = "shipment-list",
  showAdvancedFilters = true,
}) => {
  const dispatch = useDispatch();
  const { listViewState = {} } = useSelector((s) => s.shipments);
  const savedViewState = listViewState[stateKey] || {};

  const [shipments,          setShipments]         = useState([]);
  const [isLoading,          setIsLoading]          = useState(false);
  const [selectedShipments,  setSelectedShipments]  = useState([]);
  const [currentPage,        setCurrentPage]        = useState(savedViewState.currentPage   || 1);
  const [shipmentsPerPage,   setShipmentsPerPage]   = useState(savedViewState.shipmentsPerPage || 50);
  const [openDropdownRow,    setOpenDropdownRow]    = useState(null);
  const [dropdownPos,        setDropdownPos]        = useState({ top: 0, left: 0 });
  const [totalPages,         setTotalPages]         = useState(1);
  const [expandedCellKey,    setExpandedCellKey]    = useState(null);
  const [showBulkMenu,       setShowBulkMenu]       = useState(false);
  const [errorMessage,       setErrorMessage]       = useState("");
  const [isRefreshingMiles,  setIsRefreshingMiles]  = useState(false);
  const [showFilterModal,    setShowFilterModal]    = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  const [sortBy,    setSortBy]    = useState(savedViewState.sortBy    || null);
  const [ascending, setAscending] = useState(savedViewState.ascending ?? false);

  const dropdownRef      = useRef(null);
  const checkboxHeaderRef= useRef(null);
  const bulkMenuRef      = useRef(null);
  const prevFilterRef    = useRef(filter);
  const prevSearchTrigRef= useRef(searchTrigger);

  const token      = Cookies.get("authToken");
  const officeCode = Cookies.get("officeCodes");
  const navigate   = useNavigate();

  const { defaultTabs, dynamicTabs } = useSelector((s) => s.invoices);
  const { hasPrivilege }  = usePrivilege();
  const canCreateInvoice  = hasPrivilege("invoices:CREATE");

  // Filters persisted in Redux so they survive tab switching
  const { filters: advancedFilters, updateFilters, clearFilters, removePill, activeCount } =
    useShipmentFilters({ stateKey, persist: true });

  const prevQuickFilterForDirtyRef = useRef(filter);
  const prevAdvancedFiltersForDirtyRef = useRef(JSON.stringify(advancedFilters));

  const tableViews = useTableSavedViews({
    pageKey: stateKey,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", SHIPMENT_NUMBER_HEADER],
    getExtraConfig: () => ({
      sortBy,
      ascending,
      filter,
      advancedFilters,
      currentPage,
      pageSize: shipmentsPerPage,
    }),
    onApplyConfig: (config, _viewMeta, options = {}) => {
      if (config.sortBy !== undefined) setSortBy(config.sortBy);
      if (config.ascending !== undefined) setAscending(config.ascending);
      if (config.currentPage) setCurrentPage(config.currentPage);
      if (config.pageSize) setShipmentsPerPage(config.pageSize);
      if (config.filter !== undefined) {
        dispatch(setStatusFilter(config.filter));
        dispatch(setListViewState({ stateKey, filter: config.filter }));
      }
      if (config.advancedFilters !== undefined) {
        dispatch(setAdvancedFilters({
          stateKey,
          filters: config.advancedFilters ?? DEFAULT_ADVANCED_FILTERS,
        }));
      } else if (options.resetExtraConfig) {
        dispatch(setAdvancedFilters({ stateKey, filters: DEFAULT_ADVANCED_FILTERS }));
        dispatch(setStatusFilter(DEFAULT_QUICK_FILTER));
        dispatch(setListViewState({ stateKey, filter: DEFAULT_QUICK_FILTER }));
      }
    },
  });

  const {
    columnWidths: savedColumnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    reconcileDirtyState,
    // Views-driven column manager (full column set, hidden columns surface in the
    // "Hidden" section). Spread `columnManagerProps` onto <Table>; the rich panel
    // renders inside the single Views dropdown via `onManageColumns`.
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  // Mark dirty only when the user changes filters, not when activeView updates after save.
  useEffect(() => {
    if (!activeView || viewsLoading) return;

    const quickFilterChanged = prevQuickFilterForDirtyRef.current !== filter;
    const advancedFiltersJson = JSON.stringify(advancedFilters);
    const advancedFiltersChanged =
      prevAdvancedFiltersForDirtyRef.current !== advancedFiltersJson;

    prevQuickFilterForDirtyRef.current = filter;
    prevAdvancedFiltersForDirtyRef.current = advancedFiltersJson;

    if (!quickFilterChanged && !advancedFiltersChanged) return;

    reconcileDirtyState();
  }, [activeView, filter, advancedFilters, viewsLoading, reconcileDirtyState]);

  // ── Loading overlay: only show after 250ms to avoid flash on fast requests ──
  useEffect(() => {
    if (!isLoading) { setShowLoadingOverlay(false); return; }
    const t = setTimeout(() => setShowLoadingOverlay(true), 250);
    return () => clearTimeout(t);
  }, [isLoading]);

  // ── Persist sort / page state to Redux (after saved views load) ──
  // Filter is owned by saved views (onApplyConfig) and explicit toolbar actions — do not
  // mirror the global statusFilter here; it defaults to "New" and would overwrite the view.
  useEffect(() => {
    if (viewsLoading || !activeView) return;
    dispatch(setListViewState({
      stateKey,
      currentPage,
      shipmentsPerPage,
      sortBy,
      ascending,
    }));
  }, [dispatch, stateKey, currentPage, shipmentsPerPage, sortBy, ascending, viewsLoading, activeView]);

  // ── Close action dropdown on outside click ──
  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenDropdownRow(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ── Close bulk menu on outside click ──
  useEffect(() => {
    const handle = (e) => {
      if (
        bulkMenuRef.current     && !bulkMenuRef.current.contains(e.target) &&
        checkboxHeaderRef.current && !checkboxHeaderRef.current.contains(e.target)
      ) setShowBulkMenu(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ── Disabled rows (Cancelled shipments can't be actioned) ──
  const disabledByShipment = React.useMemo(() => {
    const m = new Map();
    for (const s of shipments)
      m.set(makeKey(s.shipmentNumber, s.officeCode), DISABLED_STATUSES.has((s.status || "").toUpperCase()));
    return m;
  }, [shipments]);


  const fetchShipments = useCallback(async (signal) => {
    try {
      setIsLoading(true);

      const payload = buildShipmentSearchPayload({
        searchQuery,
        filter,
        advancedFilters,
        sortBy,
        ascending,
        filterType: FILTER_TABS.ALL,
      });

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/shipment-details/partial-search?page=${currentPage - 1}&size=${shipmentsPerPage}`,
        payload,
        {
          signal,
          headers: {
            "Content-Type": "application/json",
            Authorization:  `Bearer ${token}`,
            "Office-Code":  officeCode,
          },
        }
      );

      if (signal?.aborted) return;

      setShipments(data.content);
      setTotalShipments(data.totalElements);
      setTotalPages(data.totalPages);

    } catch (err) {
      if (signal?.aborted || err?.code === "ERR_CANCELED") return;
      console.error("Error fetching shipments:", err);
      setShipments([]);
      setTotalShipments(0);
      setTotalPages(1);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [
    currentPage, shipmentsPerPage, searchQuery, filter,
    sortBy, ascending, advancedFilters, token, officeCode, setTotalShipments,
  ]);

  useEffect(() => {
    if (viewsLoading) return;

    const filterChanged = prevFilterRef.current    !== filter;
    const searchChanged = prevSearchTrigRef.current !== searchTrigger;
    const needsReset    = (filterChanged || searchChanged) && currentPage !== 1;

    prevFilterRef.current    = filter;
    prevSearchTrigRef.current= searchTrigger;

    if (needsReset) { setCurrentPage(1); return; }

    const controller = new AbortController();
    fetchShipments(controller.signal);
    return () => controller.abort();
  }, [searchTrigger, filter, currentPage, fetchShipments, viewsLoading, activeView]);

  // ── Sorting ──
  const handleSort = (newSortBy) => {
    setCurrentPage(1);
    if (sortBy !== newSortBy) { setSortBy(newSortBy); setAscending(true); }
    else setAscending((p) => !p);
    markDirty();
  };

  // ── Row formatting ──
  const formattedShipments = React.useMemo(
    () => shipments.map(formatShipmentRow),
    [shipments]
  );

  // ── Selection ──
  const handleSelectAll = (checked) => {
    if (!checked) { setSelectedShipments([]); return; }
    setSelectedShipments(
      formattedShipments
        .map((r) => makeKey(r["Shipment Number"], r["Office Code"]))
        .filter((key) => !disabledByShipment.get(key))
    );
  };

  const handleSingleCheckbox = (key) => {
    if (disabledByShipment.get(key)) return;
    setSelectedShipments((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // ── Navigation ──
  const handleViewShipment = (shipmentNumber, shipmentOfficeCode) => {
    dispatch(addTab({
      id: `view-${shipmentNumber}-${Date.now()}`,
      name: `${shipmentNumber}`,
      contentType: "shipmentView",
      shipmentNumber,
      shipmentOfficeCode,
      isDynamic: true,
      createdAt: Date.now(),
    }));
  };

  const handleEditShipment   = (id)        => console.log("Edit:",   id);
  const deleteShipment       = async (id)  => console.log("Delete:", id);

  // ── Bulk: Create Invoice ──
  const handleBulkCreateInvoice = async () => {
    setErrorMessage("");
    if (!selectedShipments.length) {
      setErrorMessage("Please select at least one shipment.");
      return;
    }

    const chosen = shipments.filter((s) =>
      selectedShipments.includes(makeKey(s.shipmentNumber, s.officeCode))
    );

    try {
      const details = await Promise.all(
        chosen.map((s) =>
          axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/api/shipments/invoice/${s.shipmentNumber}`, {
              headers: { Authorization: `Bearer ${token}`, "Office-Code": s.officeCode || officeCode },
            })
            .then((r) => r.data)
        )
      );

      const notCompleted = details.filter((s) => (s.status || "").toLowerCase() !== "completed");
      if (notCompleted.length) {
        setErrorMessage(`These shipment(s) are not Completed: ${notCompleted.map((s) => s.shipmentNumber).join(", ")}`);
        return;
      }
      const already = details.filter((s) => s.isInvoiced);
      if (already.length) {
        setErrorMessage(`Cannot invoice—already invoiced: ${already.map((s) => s.shipmentNumber).join(", ")}`);
        return;
      }
      const collect = details.filter((s) => s.paymentMethod === "COLLECT");
      if (collect.length) {
        setErrorMessage(`Cannot invoice COLLECT shipment(s): ${collect.map((s) => s.shipmentNumber).join(", ")}`);
        return;
      }

      const billTo = details[0].officeCode;
      const payTo  = details[0].carrierOfficeCode.partyOfficeCode;
      if (details.some((s) => s.officeCode !== billTo || s.carrierName !== payTo)) {
        setErrorMessage("All shipments must share the same Bill-To and Pay-To accounts.");
        return;
      }

      const prefilled = details.map((d) => {
        const shipTosFromStops = (d.shipmentStops || [])
          .filter((s) => (s.stopType || "").toUpperCase() === "DROP_OFF")
          .sort((a, b) => (a.stopSequence ?? Number.MAX_SAFE_INTEGER) - (b.stopSequence ?? Number.MAX_SAFE_INTEGER))
          .map((s) => ({
            companyName: s.stopName ?? s.warehouseName ?? null,
            address:     s.addressLine1 ?? null,
            address2:    s.addressLine2 ?? null,
            city:        s.city ?? null,
            state:       s.state ?? null,
            zip:         s.zipCode ?? null,
            country:     s.country ?? null,
            phone:       s.contactPhone ?? null,
            email:       s.contactEmail ?? null,
            fax:         s.contactFax ?? null,
            stopNumber:  s.stopNumber ?? null,
            stopSequence:s.stopSequence ?? null,
            stopType:    s.stopType ?? null,
          }));

        const shipTos = shipTosFromStops.length
          ? shipTosFromStops
          : [{
              companyName: d.receiverOfficeCode?.name ?? d.receiverName ?? null,
              address:     d.receiverOfficeCode?.addressLine1 ?? null,
              city:        d.receiverOfficeCode?.city ?? null,
              state:       d.receiverOfficeCode?.state ?? null,
              zip:         d.receiverOfficeCode?.zipCode ?? null,
              country:     d.receiverOfficeCode?.country ?? null,
              phone:       d.receiverOfficeCode?.contactPhone ?? null,
              email:       d.receiverOfficeCode?.contactEmail ?? null,
              fax:         d.receiverOfficeCode?.contactFax ?? null,
            }];

        return {
          shipmentNumber:    d.shipmentNumber,
          shipmentOfficeCode:d.officeCode,
          shipmentOrigin:    d.portOfOrigin,
          shipmentDestination:d.portOfDestination,
          equipmentNumber:   d.isoEquipmentCode,
          shipmentCharge:    d.totalCharge,
          charges: (d.charges || []).map((c) => ({
            chargeCode: c.code, chargeAmount: c.amount, chargeCurrency: c.currency,
            chargeType: c.type, chargeAuditStatus: c.chargeApprovalStatus, isAutoPopulated: true,
          })),
          estimatedCharges: (d.estimatedCharges || []).map((c) => ({
            chargeCode: c.code, chargeAmount: c.amount, chargeCurrency: c.currency,
            chargeType: c.type, chargeAuditStatus: c.chargeApprovalStatus, isAutoPopulated: true,
          })),
          billToCompanyName: fallbackName(d.shipperOfficeCode?.name),
          billToOfficeCode:  d.shipperOfficeCode?.partyOfficeCode,
          billToAddress:     d.shipperOfficeCode?.addressLine1,
          billToCity:        d.shipperOfficeCode?.city,
          billToState:       d.shipperOfficeCode?.state,
          billToZip:         d.shipperOfficeCode?.zipCode,
          billToCountry:     d.shipperOfficeCode?.country,
          billToPhone:       d.shipperOfficeCode?.contactPhone,
          billToEmail:       d.shipperOfficeCode?.contactEmail,
          billToFax:         d.shipperOfficeCode?.contactFax,
          payToCompanyName:  d.carrierName,
          payToOfficeCode:   d.carrierOfficeCode?.partyOfficeCode,
          payToAddress:      d.carrierOfficeCode?.addressLine1,
          payToCity:         d.carrierOfficeCode?.city,
          payToState:        d.carrierOfficeCode?.state,
          payToZip:          d.carrierOfficeCode?.zipCode,
          payToCountry:      d.carrierOfficeCode?.country,
          payToPhone:        d.carrierOfficeCode?.contactPhone,
          payToEmail:        d.carrierOfficeCode?.contactEmail,
          payToFax:          d.carrierOfficeCode?.contactFax,
          shipTos,
          shipToCompanyName: shipTos[0]?.companyName ?? null,
          shipToOfficeCode:  d.receiverOfficeCode?.partyOfficeCode ?? null,
          shipToAddress:     shipTos[0]?.address ?? null,
          shipToCity:        shipTos[0]?.city ?? null,
          shipToState:       shipTos[0]?.state ?? null,
          shipToZip:         shipTos[0]?.zip ?? null,
          shipToCountry:     shipTos[0]?.country ?? null,
          shipToPhone:       shipTos[0]?.phone ?? null,
          shipToEmail:       shipTos[0]?.email ?? null,
          shipToFax:         shipTos[0]?.fax ?? null,
        };
      });

      dispatch(addInvoiceTab({ id: `invoice-new-${Date.now()}`, name: "New Invoice", type: "new", data: prefilled }));
      dispatch(setActiveInvoiceTab({ index: defaultTabs.length + dynamicTabs.length }));
      navigate("/dashboard/invoices");
    } catch (err) {
      setErrorMessage(err.response?.data?.content || "Unexpected error while fetching invoice data.");
    }
  };

  // ── Bulk: Refresh Miles ──
  const handleBulkRefreshMiles = async () => {
    if (!selectedShipments.length) {
      notify.error({ title: "No Shipments Selected", message: "Please select at least one shipment." });
      setShowBulkMenu(false);
      return;
    }
    const shipmentNumbers = selectedShipments.map((k) => k.split("::")[0]);
    setIsRefreshingMiles(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/shipments/refresh-transit-miles`,
        { shipmentNumbers },
        { headers: { Authorization: `Bearer ${token}`, "Office-Code": officeCode } }
      );
      const { totalFound, successfullyUpdated, failed, noChange, failures } = data;
      notify.success({
        title:   "Miles Refreshed",
        message: `Total: ${totalFound} | Updated: ${successfullyUpdated} | Unchanged: ${noChange} | Failed: ${failed}`,
        options: { autoClose: 4000 },
      });
      if (failed > 0)
        notify.error({ title: `${failed} Shipment(s) Failed`, message: failures?.map((f) => f.shipmentNumber).join(", ") });
      setSelectedShipments([]);
      fetchShipments();
    } catch (err) {
      notify.error({ title: "Refresh Failed", message: err?.response?.data?.message || "Could not refresh miles." });
    } finally {
      setIsRefreshingMiles(false);
      setShowBulkMenu(false);
    }
  };

  // ── Action dropdown (portal-based to escape stacking contexts) ──
  const handleActionClick = (key, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === key ? null : key));
  };

  const renderActionColumn = (row, disabled, getStickyCellProps) => {
    const key   = makeKey(row["Shipment Number"], row["Office Code"]);
    const id    = row["Shipment Number"];
    const shipOC= row["Office Code"];
    const isOpen= openDropdownRow === key;
    const { className: stickyClass, style: stickyStyle } = getStickyCellProps("Action");

    const actions = disabled
      ? [{ icon: "Eye",   label: "View",   color: "blue",  action: () => handleViewShipment(id, shipOC) }]
      : [
          { icon: "Eye",   label: "View",   color: "blue",  action: () => handleViewShipment(id, shipOC) },
          { icon: "Edit",  label: "Edit",   color: "green", action: () => handleEditShipment(id, shipOC) },
          { icon: "Trash", label: "Delete", color: "red",   action: () => deleteShipment(id, shipOC) },
        ];

    return (
      <>
        <td
          onClick={(e) => e.stopPropagation()}
          className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${stickyClass}`}
          style={{
            ...stickyStyle,
            width: `${ACTION_COLUMN_WIDTH}px`, minWidth: `${ACTION_COLUMN_WIDTH}px`, maxWidth: `${ACTION_COLUMN_WIDTH}px`,
          }}
        >
          <button
            data-action-trigger
            className={`p-1 relative w-full transition-none ${
              disabled
                ? "opacity-60 hover:opacity-80"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text"
            }`}
            onClick={(e) => handleActionClick(key, e)}
          >
            <MoreHorizontal size={18} className="mx-auto" />
          </button>
        </td>

        {isOpen && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
          >
            <ActionMenu
              rowId={key} isOpen onClose={() => setOpenDropdownRow(null)}
              moduleName="Shipment" actions={actions}
            />
          </div>,
          document.body
        )}
      </>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">

      {/* Advanced filter toolbar (button + pills) */}
      {showAdvancedFilters ? (
        <AdvancedFilterToolbar
          filters={advancedFilters}
          activeCount={activeCount}
          onOpenModal={() => setShowFilterModal(true)}
          onClearAll={() => { clearFilters(); setCurrentPage(1); markDirty(); }}
          onRemovePill={(key) => { removePill(key); setCurrentPage(1); markDirty(); }}
          trailing={
            <TableSavedViewsControl
              controlLabel={controlLabel}
              views={views}
              activeView={activeView}
              loading={viewsLoading}
              error={viewsError}
              isDirty={isDirty}
              allDataHeaders={TABLE_HEADERS.slice(2)}
              dataHeaders={dataHeaders}
              onSelectView={selectView}
              onSaveNew={saveNewView}
              onUpdateActive={updateActiveView}
              onRename={renameView}
              onDelete={deleteView}
              onSetDefault={setDefaultView}
              onUpdateColumnVisibility={updateColumnVisibility}
              onManageColumns={onManageColumns}
            />
          }
        />
      ) : (
        <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
          <TableSavedViewsControl
            controlLabel={controlLabel}
            views={views}
            activeView={activeView}
            loading={viewsLoading}
            error={viewsError}
            isDirty={isDirty}
            allDataHeaders={TABLE_HEADERS.slice(2)}
            dataHeaders={dataHeaders}
            onSelectView={selectView}
            onSaveNew={saveNewView}
            onUpdateActive={updateActiveView}
            onRename={renameView}
            onDelete={deleteView}
            onSetDefault={setDefaultView}
            onUpdateColumnVisibility={updateColumnVisibility}
            onManageColumns={onManageColumns}
          />
        </div>
      )}

      {/* Table */}
      <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none relative">
        <Table
          {...columnManagerProps}
          data={formattedShipments}
          initialColumnWidths={savedColumnWidths}
          onColumnResize={onColumnResize}
          onSelectAll={handleSelectAll}
          renderCheckboxActions={() => setShowBulkMenu((p) => !p)}
          checkboxHeaderRef={checkboxHeaderRef}
          selectedRows={selectedShipments}
          totalValidRows={formattedShipments.filter(
            (r) => !disabledByShipment.get(makeKey(r["Shipment Number"], r["Office Code"]))
          ).length}
          autoGrowColumns
          dataColumnMinWidth={DATA_COLUMN_MIN_WIDTH}
          dataColumnMaxWidth={DATA_COLUMN_MAX_WIDTH}
          enableColumnResize
          enableColumnReorder
          enableColumnPin
          nonHideableColumnIds={[SHIPMENT_NUMBER_HEADER]}
          sortBy={sortBy}
          ascending={ascending}
          onSort={(h) => { const k = SORTABLE_COLUMNS[h]; if (k) handleSort(k); }}
          sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
          sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
        >
          {(row, _rowIdx, columnWidths, getStickyCellProps, visibleColumns) => {
            const sn      = row["Shipment Number"];
            const key     = makeKey(sn, row["Office Code"]);
            const disabled= disabledByShipment.get(key);
            const { className: checkboxClass, style: checkboxStyle } = getStickyCellProps("Selection");
            const orderedDataHeaders = (visibleColumns ?? [])
              .map((col) => col.id)
              .filter((id) => id !== "Selection" && id !== "Action");
            const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

            return (
              <>
                {/* Selection */}
                <td
                  className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${checkboxClass}`}
                  style={{
                    ...checkboxStyle,
                    width: `${SELECTION_COLUMN_WIDTH}px`, minWidth: `${SELECTION_COLUMN_WIDTH}px`, maxWidth: `${SELECTION_COLUMN_WIDTH}px`,
                  }}
                >
                  <input
                    type="checkbox"
                    className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                    checked={selectedShipments.includes(key)}
                    disabled={disabled}
                    onChange={() => handleSingleCheckbox(key)}
                  />
                </td>

                {/* Action (portal dropdown) */}
                {renderActionColumn(row, disabled, getStickyCellProps)}

                {/* Data columns */}
                {dataHeadersToRender.map((header) => {
                  const cellKey          = `${key}-${header}`;
                  const isExpanded       = expandedCellKey === cellKey;
                  const { className: colClass, style: colStyle } = getStickyCellProps(header);

                  return (
                    <td
                      key={header}
                      className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${
                        colClass
                      }`}
                      onDoubleClick={() => handleViewShipment(sn, row["Office Code"])}
                      onMouseEnter={() => setExpandedCellKey(cellKey)}
                      onMouseLeave={() => setExpandedCellKey(null)}
                      style={{
                        ...colStyle,
                        whiteSpace:    isExpanded ? "normal"   : "nowrap",
                        overflow:      "hidden",
                        textOverflow:  "ellipsis",
                        minWidth:      `${columnWidths?.[header] || DATA_COLUMN_MIN_WIDTH}px`,
                        maxWidth:      isExpanded ? `${DATA_COLUMN_MAX_WIDTH * 2}px` : `${DATA_COLUMN_MAX_WIDTH}px`,
                        width:         columnWidths?.[header] ? `${columnWidths[header]}px` : `${DATA_COLUMN_MIN_WIDTH}px`,
                        transition:    "max-width 120ms ease",
                      }}
                    >
                      {row[header]}
                    </td>
                  );
                })}
              </>
            );
          }}
        </Table>

        {/* Bulk action menu */}
        {showBulkMenu && checkboxHeaderRef.current && (
          <div
            ref={bulkMenuRef}
            className="absolute z-[1000]"
            style={{
              top:  checkboxHeaderRef.current.getBoundingClientRect().bottom + window.scrollY + 6,
              left: checkboxHeaderRef.current.getBoundingClientRect().left  + window.scrollX,
            }}
          >
            <ActionMenu
              rowId="bulk" isOpen onClose={() => setShowBulkMenu(false)}
              moduleName="Shipment" placement="header"
              actions={[
                ...(canCreateInvoice ? [{
                  icon: "FilePlus", label: "Create Invoice", color: "blue",
                  action: async () => { await handleBulkCreateInvoice(); setShowBulkMenu(false); },
                }] : []),
                {
                  icon: "RefreshCw",
                  label: isRefreshingMiles ? "Refreshing..." : "Refresh Miles",
                  color: "green",
                  action: handleBulkRefreshMiles,
                },
              ]}
            />
          </div>
        )}

        {/* Loading overlay */}
        {showLoadingOverlay && (
          <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-white/60 dark:bg-black/30">
            <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message="Loading Shipments..." />
          </div>
        )}
      </div>

      <GlobalPagination
        label="Shipments per page"
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={shipmentsPerPage}
        onItemsPerPageChange={setShipmentsPerPage}
      />

      {/* Advanced filter modal */}
      {showFilterModal && (
        <AdvancedFilterModal
          filters={advancedFilters}
          quickFilterStatus={filter} 
          onApply={(f) => {
            updateFilters(f);
            if (f.statuses?.length) {
              dispatch(setStatusFilter(null));
              dispatch(setListViewState({ stateKey, filter: null }));
            }
            setCurrentPage(1);
            markDirty();
          }}
          onClose={() => setShowFilterModal(false)}
        />
      )}
    </div>
  );
};

export default ShipmentsTable;